<template>
  <div
    class="notif-item"
    :class="{ 'notif-item--unread': unread, 'notif-item--compact': compact }"
    :style="{ '--notif-color': severity.color }">
    <span class="notif-item__icon" aria-hidden="true">
      <i :class="category.icon"></i>
    </span>
    <div class="notif-item__main">
      <div class="notif-item__head">
        <span class="notif-item__title">{{ item.title }}</span>
        <span v-if="unread" class="notif-item__dot" title="Unread"><span class="sr-only">Unread</span></span>
      </div>
      <div class="notif-item__tags">
        <span class="notif-item__severity">{{ severity.label }}</span>
        <b-badge v-if="phase" :variant="phase.variant" class="notif-item__phase">{{ phase.label }}</b-badge>
      </div>
      <p class="notif-item__body">{{ item.body }}</p>
      <div class="notif-item__meta">
        <span>{{ item.compName }}</span>
        <template v-if="item.massName">
          <span aria-hidden="true">·</span>
          <span>{{ item.massName }}</span>
        </template>
        <span aria-hidden="true">·</span>
        <time :datetime="item.occurredAt" :title="fullTime">{{ relative }}</time>
      </div>
    </div>
  </div>
</template>

<script>
import { formatApiDatetime } from '@/util/apiDatetime';
import {
  severityMeta, categoryMeta, phaseBadge, relativeTime, isUnread,
} from '@/util/notificationInbox';

export default {
  name: 'NotificationItem',
  props: {
    item: { type: Object, required: true },
    nowMs: { type: Number, required: true },
    compact: { type: Boolean, default: false },
  },
  computed: {
    severity() {
      return severityMeta(this.item.severity);
    },
    category() {
      return categoryMeta(this.item.category);
    },
    phase() {
      return phaseBadge(this.item.phase);
    },
    unread() {
      return isUnread(this.item);
    },
    relative() {
      return relativeTime(this.item.occurredAt, this.nowMs);
    },
    fullTime() {
      return formatApiDatetime(this.item.occurredAt);
    },
  },
};
</script>

<style scoped>
.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding-left: 10px;
  border-left: 3px solid var(--notif-color);
  color: #1f2d3d;
  text-align: left;
}

.notif-item__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #f1f4f9;
  color: var(--notif-color);
  font-size: 16px;
}

.notif-item__main {
  flex: 1 1 auto;
  min-width: 0;
}

.notif-item__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.notif-item__title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.notif-item--unread .notif-item__title {
  font-weight: 700;
}

.notif-item__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background-color: #0d6efd;
}

.notif-item__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.notif-item__severity {
  color: var(--notif-color);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.notif-item__phase {
  font-size: 10px;
}

.notif-item__body {
  margin: 4px 0 0;
  color: #4a5568;
  font-size: 13px;
  line-height: 1.4;
  white-space: normal;
  overflow-wrap: anywhere;
}

.notif-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  color: #8a94a6;
  font-size: 11px;
}

.notif-item:not(.notif-item--unread) {
  opacity: 0.6;
}

.notif-item--compact .notif-item__title {
  font-size: 13px;
}

.notif-item--compact .notif-item__body {
  display: -webkit-box;
  overflow: hidden;
  font-size: 12px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
